import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useLang } from "../contexts/LanguageContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Progress } from "../components/ui/progress";
import SpeakButton from "../components/SpeakButton";
import OpenAccountBlock from "../components/OpenAccountBlock";
import { api } from "../lib/api";
import {
  buildGrowthSeries,
  projectValue,
  recommendFor,
  formatMoney,
  currencyForCountry,
  OPTIONS,
} from "../lib/finance";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";
import {
  ArrowRight,
  ArrowLeft,
  Wallet,
  PiggyBank,
  LineChart,
  ShieldCheck,
  Sparkles,
  BookOpen,
  Check,
  Printer,
  UserPlus,
} from "lucide-react";
import { toast } from "sonner";

const STEPS = 5;
const GUEST_KEY = "sereno_guest_profile";

function loadGuestProfile() {
  try {
    return JSON.parse(localStorage.getItem(GUEST_KEY) || "{}");
  } catch {
    return {};
  }
}
function saveGuestProfile(patch) {
  const current = loadGuestProfile();
  localStorage.setItem(GUEST_KEY, JSON.stringify({ ...current, ...patch }));
}

export default function Onboarding() {
  const { user, updateProfile } = useAuth();
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);

  const guest = !user;
  const guestData = guest ? loadGuestProfile() : {};
  const seed = guest ? guestData : user?.profile || {};
  const country = guest ? guestData.country || "FR" : user?.country || "FR";

  const [income, setIncome] = useState(seed.monthly_income || "");
  const [expenses, setExpenses] = useState(seed.monthly_expenses || "");
  const [monthly, setMonthly] = useState(seed.monthly_savings || 50);
  const [initial, setInitial] = useState(seed.initial_amount || 0);
  const [risk, setRisk] = useState(seed.risk_profile || "prudent");
  const [horizon, setHorizon] = useState(seed.horizon_years || 10);

  const currency = currencyForCountry(country);
  const remaining = Math.max(0, Number(income || 0) - Number(expenses || 0));

  const suggestedOption = useMemo(
    () => recommendFor({ risk_profile: risk, country }),
    [risk, country]
  );

  const projections = useMemo(
    () =>
      [5, 8, 10].map((y) => ({
        years: y,
        value: projectValue({
          initial: Number(initial || 0),
          monthly: Number(monthly || 0),
          years: y,
          annualRate: suggestedOption.rate,
        }),
      })),
    [initial, monthly, suggestedOption.rate]
  );

  const series = useMemo(
    () =>
      buildGrowthSeries({
        initial: Number(initial || 0),
        monthly: Number(monthly || 0),
        years: horizon,
        annualRate: suggestedOption.rate,
      }),
    [initial, monthly, horizon, suggestedOption.rate]
  );

  useEffect(() => {
    // Guest mode is allowed on /decouvrir. No forced redirect.
  }, []);

  const saveProgress = async (patch) => {
    if (guest) {
      saveGuestProfile(patch);
      return;
    }
    try {
      await updateProfile(patch);
    } catch {
      // silent
    }
  };

  const goNext = async () => {
    if (step === 1) {
      await saveProgress({
        monthly_income: Number(income || 0),
        monthly_expenses: Number(expenses || 0),
        tutorial_step: 2,
      });
    } else if (step === 2) {
      await saveProgress({
        monthly_savings: Number(monthly || 0),
        initial_amount: Number(initial || 0),
        tutorial_step: 3,
      });
    } else if (step === 3) {
      await saveProgress({ horizon_years: horizon, tutorial_step: 4 });
    } else if (step === 4) {
      await saveProgress({ risk_profile: risk, tutorial_step: 5 });
    }
    setStep((s) => Math.min(STEPS, s + 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const goBack = () => {
    setStep((s) => Math.max(1, s - 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const saveRecommendation = async () => {
    const projected = projectValue({
      initial: Number(initial || 0),
      monthly: Number(monthly || 0),
      years: horizon,
      annualRate: suggestedOption.rate,
    });
    if (guest) {
      // Persist last calc into guest storage then push to register
      saveGuestProfile({
        monthly_income: Number(income || 0),
        monthly_expenses: Number(expenses || 0),
        monthly_savings: Number(monthly || 0),
        initial_amount: Number(initial || 0),
        horizon_years: horizon,
        risk_profile: risk,
        pending_reco: {
          recommendation_id: suggestedOption.id,
          monthly_amount: Number(monthly || 0),
          initial_amount: Number(initial || 0),
          horizon_years: horizon,
          projected_value: projected,
        },
      });
      navigate("/register");
      return;
    }
    try {
      await api.post("/recommendations/save", {
        recommendation_id: suggestedOption.id,
        monthly_amount: Number(monthly || 0),
        initial_amount: Number(initial || 0),
        horizon_years: horizon,
        projected_value: projected,
      });
      await updateProfile({ tutorial_completed: true, tutorial_step: STEPS });
      toast.success(lang === "fr" ? "Recommandation enregistrée" : "Recommendation saved");
      navigate("/dashboard");
    } catch {
      toast.error(t("err_generic"));
    }
  };

  const handlePrint = () => {
    if (typeof window !== "undefined") window.print();
  };

  return (
    <div className="mx-auto max-w-3xl px-6 py-10" data-testid="onboarding-page">
      {guest && (
        <div
          data-testid="guest-banner"
          className="mb-6 rounded-2xl border-2 border-[#A762B1]/30 bg-[#A762B1]/10 p-4 text-base text-[#6E3978]"
        >
          {t("guest_banner")}
        </div>
      )}

      <div className="mb-8" data-testid="progress-block">
        <div className="mb-3 flex items-center justify-between text-base text-[#4A4A4A]">
          <span>
            {t("step_of")} <strong className="text-[#101A42]">{step}</strong> {t("of")}{" "}
            {STEPS}
          </span>
          <span>{Math.round((step / STEPS) * 100)}%</span>
        </div>
        <Progress value={(step / STEPS) * 100} className="h-3" data-testid="progress-bar" />
      </div>

      {step === 1 && (
        <StepCard
          icon={Wallet}
          title={t("step1_title")}
          intro={t("step1_intro")}
          testid="step-1"
          speakText={`${t("step1_title")}. ${t("step1_intro")}`}
        >
          <div className="space-y-6">
            <MoneyInput
              id="income"
              testid="income-input"
              label={t("monthly_income")}
              value={income}
              onChange={setIncome}
              currency={currency}
              lang={lang}
            />
            <MoneyInput
              id="expenses"
              testid="expenses-input"
              label={t("monthly_expenses")}
              value={expenses}
              onChange={setExpenses}
              currency={currency}
              lang={lang}
            />
            <div
              className="rounded-2xl border-2 border-[#101A42]/15 bg-[#101A42]/5 p-6"
              data-testid="remaining-box"
            >
              <p className="text-lg text-[#101A42]">{t("calc_savings")}</p>
              <p className="mt-2 font-serif text-4xl font-semibold text-[#101A42]">
                {formatMoney(remaining, currency, lang)}
              </p>
            </div>
          </div>
        </StepCard>
      )}

      {step === 2 && (
        <StepCard
          icon={PiggyBank}
          title={t("step2_title")}
          intro={t("step2_intro")}
          testid="step-2"
          speakText={`${t("step2_title")}. ${t("step2_intro")}`}
        >
          <div className="space-y-6">
            <div>
              <Label className="mb-2 block text-lg font-medium">
                {t("monthly_savings")}
              </Label>
              <div className="flex flex-wrap gap-3">
                {[20, 50, 100, 200, 500].map((v) => (
                  <button
                    key={v}
                    type="button"
                    data-testid={`preset-monthly-${v}`}
                    onClick={() => setMonthly(v)}
                    className={`min-h-[56px] rounded-full border-2 px-6 text-lg font-semibold transition-colors ${
                      Number(monthly) === v
                        ? "border-[#A762B1] bg-[#A762B1] text-white"
                        : "border-[#101A42]/25 bg-white text-[#101A42] hover:border-[#A762B1]"
                    }`}
                  >
                    {formatMoney(v, currency, lang)}
                  </button>
                ))}
              </div>
              <div className="mt-4">
                <MoneyInput
                  id="monthly"
                  testid="monthly-input"
                  label={
                    lang === "fr"
                      ? "Ou entrez un autre montant"
                      : "Or enter a different amount"
                  }
                  value={monthly}
                  onChange={setMonthly}
                  currency={currency}
                  lang={lang}
                />
              </div>
            </div>
            <MoneyInput
              id="initial"
              testid="initial-input"
              label={t("initial_amount")}
              value={initial}
              onChange={setInitial}
              currency={currency}
              lang={lang}
            />
          </div>
        </StepCard>
      )}

      {step === 3 && (
        <StepCard
          icon={LineChart}
          title={t("step3_title")}
          intro={t("step3_intro")}
          testid="step-3"
          speakText={`${t("step3_title")}. ${t("step3_intro")}`}
        >
          <div className="grid gap-4 sm:grid-cols-3" data-testid="projection-cards">
            {projections.map((p) => (
              <button
                type="button"
                key={p.years}
                onClick={() => setHorizon(p.years)}
                data-testid={`horizon-${p.years}`}
                className={`rounded-2xl border-2 p-6 text-left transition-colors ${
                  horizon === p.years
                    ? "border-[#A762B1] bg-[#A762B1]/10"
                    : "border-[#E8DFC7] bg-white hover:border-[#A762B1]/50"
                }`}
              >
                <p className="text-base font-medium text-[#4A4A4A]">
                  {t(`in_${p.years}_years`)}
                </p>
                <p className="mt-2 font-serif text-3xl font-semibold text-[#101A42]">
                  {formatMoney(p.value, currency, lang)}
                </p>
              </button>
            ))}
          </div>

          <div
            className="mt-8 rounded-2xl border-2 border-[#E8DFC7] bg-white p-4 sm:p-6"
            data-testid="growth-chart"
          >
            <ResponsiveContainer width="100%" height={260}>
              <AreaChart data={series} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="growth" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#101A42" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="#101A42" stopOpacity={0.05} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="#E8DFC7" vertical={false} />
                <XAxis
                  dataKey="year"
                  tick={{ fontSize: 14, fill: "#4A4A4A" }}
                  tickFormatter={(y) => `${y} ${t("years")}`}
                />
                <YAxis
                  tick={{ fontSize: 14, fill: "#4A4A4A" }}
                  tickFormatter={(v) => formatMoney(v, currency, lang)}
                  width={90}
                />
                <Tooltip
                  formatter={(v) => formatMoney(v, currency, lang)}
                  labelFormatter={(y) => `${y} ${t("years")}`}
                  contentStyle={{
                    background: "#fff",
                    border: "2px solid #101A42",
                    borderRadius: 12,
                    fontSize: 14,
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="value"
                  stroke="#101A42"
                  strokeWidth={4}
                  fill="url(#growth)"
                />
              </AreaChart>
            </ResponsiveContainer>
            <p className="mt-4 text-base text-[#4A4A4A]">
              {lang === "fr"
                ? `Estimation basée sur un rendement moyen de ${(suggestedOption.rate * 100).toFixed(1)}% par an.`
                : `Estimate based on an average return of ${(suggestedOption.rate * 100).toFixed(1)}% per year.`}
            </p>
          </div>
        </StepCard>
      )}

      {step === 4 && (
        <StepCard
          icon={ShieldCheck}
          title={t("step4_title")}
          intro={t("step4_intro")}
          testid="step-4"
          speakText={`${t("step4_title")}. ${t("step4_intro")}`}
        >
          <div className="grid gap-4">
            {[
              { key: "prudent", color: "#A762B1" },
              { key: "equilibre", color: "#E59D32" },
              { key: "dynamique", color: "#A762B1" },
            ].map(({ key, color }) => (
              <button
                type="button"
                key={key}
                onClick={() => setRisk(key)}
                data-testid={`risk-${key}`}
                className={`flex items-start gap-4 rounded-2xl border-2 p-6 text-left transition-colors ${
                  risk === key
                    ? "border-[#101A42] bg-[#101A42]/5"
                    : "border-[#E8DFC7] bg-white hover:border-[#101A42]/50"
                }`}
              >
                <div
                  className="mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-full"
                  style={{ backgroundColor: color, color: "#fff" }}
                >
                  {risk === key && <Check className="h-5 w-5" strokeWidth={3} />}
                </div>
                <div>
                  <p className="font-serif text-2xl font-semibold text-[#101A42]">
                    {t(`risk_${key}`)}
                  </p>
                  <p className="mt-1 text-lg text-[#4A4A4A]">
                    {t(`risk_${key}_desc`)}
                  </p>
                </div>
              </button>
            ))}
          </div>
        </StepCard>
      )}

      {step === 5 && (
        <StepCard
          icon={Sparkles}
          title={t("step5_title")}
          intro=""
          testid="step-5"
          speakText={`${suggestedOption[lang]?.name || suggestedOption.fr.name}. ${
            suggestedOption[lang]?.short || suggestedOption.fr.short
          } ${suggestedOption[lang]?.why || suggestedOption.fr.why} ${
            suggestedOption[lang]?.example || suggestedOption.fr.example
          }`}
        >
          <RecommendationCard
            option={suggestedOption}
            projections={projections}
            horizon={horizon}
            monthly={Number(monthly || 0)}
            initial={Number(initial || 0)}
            currency={currency}
            lang={lang}
            t={t}
          />
          <div className="mt-8 flex flex-col gap-3 sm:flex-row" data-print-hide="true">
            {guest ? (
              <Button
                onClick={saveRecommendation}
                data-testid="guest-signup-btn"
                className="min-h-[64px] flex-1 rounded-full bg-[#A762B1] text-xl font-semibold text-white hover:bg-[#B775C0]"
              >
                <UserPlus className="mr-2 h-6 w-6" />
                {t("signup_to_save")}
              </Button>
            ) : (
              <Button
                onClick={saveRecommendation}
                data-testid="save-reco-btn"
                className="min-h-[64px] flex-1 rounded-full bg-[#A762B1] text-xl font-semibold text-white hover:bg-[#B775C0]"
              >
                {t("save_reco")}
              </Button>
            )}
            <Button
              variant="outline"
              onClick={handlePrint}
              data-testid="print-btn"
              className="min-h-[64px] rounded-full border-2 border-[#101A42] text-lg font-semibold text-[#101A42] hover:bg-[#101A42] hover:text-white"
            >
              <Printer className="mr-2 h-5 w-5" />
              {t("print_pdf")}
            </Button>
            {!guest && (
              <Button
                variant="ghost"
                onClick={() => navigate("/dashboard")}
                data-testid="skip-save-btn"
                className="min-h-[64px] rounded-full text-lg font-semibold text-[#101A42]"
              >
                {t("view_dashboard")}
              </Button>
            )}
          </div>
        </StepCard>
      )}

      {step < STEPS && (
        <div className="mt-10 flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={goBack}
            disabled={step === 1}
            data-testid="prev-step-btn"
            className="min-h-[56px] rounded-full text-lg text-[#101A42]"
          >
            <ArrowLeft className="mr-2 h-5 w-5" />
            {t("back")}
          </Button>
          <Button
            onClick={goNext}
            data-testid="next-step-btn"
            className="min-h-[64px] rounded-full bg-[#101A42] px-8 text-xl font-semibold text-white hover:bg-[#1A2650]"
          >
            {t("next")}
            <ArrowRight className="ml-2 h-6 w-6" />
          </Button>
        </div>
      )}
    </div>
  );
}

function StepCard({ icon: Icon, title, intro, testid, children, speakText }) {
  return (
    <section
      data-testid={testid}
      className="rounded-3xl border border-[#E8DFC7] bg-white p-6 shadow-[0_8px_30px_rgba(30,63,51,0.06)] sm:p-10"
    >
      <div className="mb-4 flex items-center justify-between gap-4">
        <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#A762B1]/15 text-[#A762B1]">
          <Icon className="h-7 w-7" strokeWidth={2} />
        </div>
        {speakText && (
          <div data-print-hide="true">
            <SpeakButton text={speakText} testid={`${testid}-speak`} />
          </div>
        )}
      </div>
      <h2 className="font-serif text-3xl font-semibold text-[#101A42] sm:text-4xl">
        {title}
      </h2>
      {intro && (
        <p className="mt-3 text-lg leading-relaxed text-[#4A4A4A]">{intro}</p>
      )}
      <div className="mt-8">{children}</div>
    </section>
  );
}

function MoneyInput({ id, testid, label, value, onChange, currency, lang }) {
  const symbol = currency === "USD" ? "$" : currency === "GBP" ? "£" : "€";
  return (
    <div>
      <Label htmlFor={id} className="mb-2 block text-lg font-medium">
        {label}
      </Label>
      <div className="relative">
        <span className="pointer-events-none absolute left-5 top-1/2 -translate-y-1/2 text-xl text-[#4A4A4A]">
          {symbol}
        </span>
        <Input
          id={id}
          data-testid={testid}
          type="number"
          inputMode="numeric"
          min="0"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="h-16 rounded-xl border-2 pl-10 text-xl"
          placeholder="0"
          aria-label={label}
        />
      </div>
    </div>
  );
}

function RecommendationCard({
  option,
  projections,
  horizon,
  monthly,
  initial,
  currency,
  lang,
  t,
}) {
  const localized = option[lang] || option.fr;
  const projected =
    projections.find((p) => p.years === horizon)?.value ||
    projections[projections.length - 1].value;
  return (
    <div
      data-testid="recommendation-card"
      className="rounded-2xl border-2 border-[#101A42] bg-gradient-to-b from-[#F8EFD9] to-white p-6 sm:p-8"
    >
      <p className="text-base font-semibold uppercase tracking-widest text-[#A762B1]">
        {lang === "fr" ? "Recommandation pour vous" : "Recommendation for you"}
      </p>
      <h3
        className="mt-2 font-serif text-3xl font-semibold text-[#101A42] sm:text-4xl"
        data-testid="reco-name"
      >
        {localized.name}
      </h3>
      <p className="mt-2 text-lg text-[#4A4A4A]">{localized.short}</p>

      <div className="mt-6 rounded-xl bg-white p-5 border border-[#E8DFC7]">
        <p className="text-base font-semibold uppercase tracking-wider text-[#101A42]">
          {t("why_this")}
        </p>
        <p className="mt-2 text-lg leading-relaxed text-[#101A42]">{localized.why}</p>
      </div>

      <div className="mt-4 rounded-xl bg-[#101A42] p-5 text-white">
        <p className="flex items-center gap-2 text-base font-semibold uppercase tracking-wider text-[#F8EFD9]">
          <BookOpen className="h-4 w-4" />
          {t("concrete_example")}
        </p>
        <p className="mt-2 text-lg leading-relaxed">{localized.example}</p>
      </div>

      <div className="mt-6 grid gap-4 sm:grid-cols-3">
        {projections.map((p) => (
          <div
            key={p.years}
            data-testid={`reco-proj-${p.years}`}
            className={`rounded-xl border-2 p-4 ${
              p.years === horizon
                ? "border-[#A762B1] bg-[#A762B1]/5"
                : "border-[#E8DFC7] bg-white"
            }`}
          >
            <p className="text-base text-[#4A4A4A]">
              {t(`in_${p.years}_years`)}
            </p>
            <p className="mt-1 font-serif text-2xl font-semibold text-[#101A42]">
              {formatMoney(p.value, currency, lang)}
            </p>
          </div>
        ))}
      </div>

      <p className="mt-6 text-sm text-[#4A4A4A]">
        {lang === "fr"
          ? `Avec ${formatMoney(monthly, currency, lang)}/mois${
              initial > 0 ? ` et ${formatMoney(initial, currency, lang)} de départ` : ""
            }, votre épargne pourrait valoir environ ${formatMoney(
              projected,
              currency,
              lang
            )} dans ${horizon} ans.`
          : `With ${formatMoney(monthly, currency, lang)}/mo${
              initial > 0 ? ` and ${formatMoney(initial, currency, lang)} to start` : ""
            }, your savings could be worth around ${formatMoney(
              projected,
              currency,
              lang
            )} in ${horizon} years.`}
      </p>

      <OpenAccountBlock optionId={option.id} />
    </div>
  );
}

// Keep OPTIONS referenced so bundlers don't tree-shake unused languages
export { OPTIONS };
